// Página PÚBLICA de verificación de recetas médicas (QR del impreso / PDF firmado).
// Ruta /rx/:codigo — fuera del login (patrón /verificar-contrato-crm).
// Datos mínimos LOPDP: paciente solo INICIALES, sin diagnóstico ni medicamentos
// (el papel/PDF que porta el paciente ya los trae; aquí solo se valida autenticidad).
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock, Loader2, ShieldQuestion, XCircle } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

interface Verificacion {
  valida: boolean;
  anulada?: boolean;
  firma_estado?: 'pendiente' | 'generando' | 'firmada' | 'error' | 'sin_firma_electronica';
  firmante?: string | null; firmada_at?: string | null;
  codigo?: string; fecha?: string; valido_hasta?: string | null; n_items?: number;
  medico?: string; medico_licencia?: string | null; especialidad?: string | null;
  empresa?: string; paciente_iniciales?: string | null;
}

export function VerificarReceta() {
  const { codigo } = useParams<{ codigo: string }>();
  const { data, isFetching, isError } = useQuery<Verificacion>({
    queryKey: ['verificar-receta', codigo],
    queryFn: async () => (await apiClient.post<Verificacion>('/rpc/med_receta_verificar', { _codigo: codigo })).data,
    enabled: !!codigo,
    retry: 1,
  });

  const firmada = data?.firma_estado === 'firmada';
  const vencida = !!data?.valido_hasta && new Date(`${data.valido_hasta}T23:59:59`) < new Date();

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-card border rounded-xl shadow-sm p-8 space-y-5">
        <div className="text-center space-y-1">
          <h1 className="text-lg font-semibold">Verificación de receta médica</h1>
          <p className="text-xs text-muted-foreground">Documento emitido electrónicamente · Plataforma ERP</p>
        </div>

        {isFetching && <div className="py-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" /></div>}

        {isError && (
          <div className="text-center space-y-2 py-4">
            <ShieldQuestion className="w-12 h-12 mx-auto text-pendiente" />
            <p className="text-sm text-muted-foreground">No se pudo consultar la verificación. Intenta de nuevo.</p>
          </div>
        )}

        {data && data.valida && data.anulada && (
          <div className="text-center space-y-2 py-4">
            <XCircle className="w-14 h-14 mx-auto text-urgente" />
            <div className="text-xl font-bold text-urgente">RECETA ANULADA</div>
            <p className="text-sm text-muted-foreground">La receta {data.codigo} existe pero fue <b>anulada</b> por el emisor. No debe dispensarse.</p>
          </div>
        )}

        {data && data.valida && !data.anulada && (
          <>
            <div className="text-center space-y-1">
              {firmada
                ? <CheckCircle2 className="w-14 h-14 mx-auto text-estable" />
                : <Clock className="w-14 h-14 mx-auto text-pendiente" />}
              <div className={`text-xl font-bold ${firmada ? 'text-estable' : 'text-pendiente'}`}>
                {firmada ? 'RECETA VÁLIDA' : 'RECETA REGISTRADA'}
              </div>
              <p className="text-xs text-muted-foreground">
                {firmada
                  ? 'Firmada electrónicamente con certificado digital (.p12)'
                  : data.firma_estado === 'pendiente' || data.firma_estado === 'generando'
                    ? 'La firma electrónica está en proceso'
                    : 'Emitida por el sistema (sin firma electrónica; verifique la firma manuscrita)'}
              </p>
              {vencida && <p className="text-xs font-medium text-urgente">⚠ Fuera de vigencia (venció el {data.valido_hasta})</p>}
            </div>
            <div className="text-sm space-y-1.5 border-t pt-4">
              {[['Receta', data.codigo],
                ['Emitida', data.fecha ? new Date(data.fecha).toLocaleDateString(LOCALE) : '—'],
                ['Válida hasta', data.valido_hasta ?? '—'],
                ['Médico', data.medico],
                ['Licencia', data.medico_licencia ?? '—'],
                ['Especialidad', data.especialidad ?? '—'],
                ['Establecimiento', data.empresa],
                ['Paciente', data.paciente_iniciales ?? '—'],
                ['Medicamentos', data.n_items != null ? `${data.n_items} ítem(s)` : '—'],
                ...(firmada ? [['Firmada por', data.firmante ?? '—'],
                  ['Firmada el', data.firmada_at ? new Date(data.firmada_at).toLocaleString(LOCALE, { hour12: false }) : '—']] : []),
              ].map(([k, v]) => (
                <div key={k as string} className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="text-right font-medium">{v}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground border-t pt-3">
              Coteje el número de receta y la cantidad de ítems con el documento presentado.
              {firmada && ' La validez criptográfica completa puede comprobarse abriendo el PDF firmado en Adobe Reader o en el validador oficial FirmaEC.'}
            </p>
          </>
        )}

        {data && !data.valida && (
          <div className="text-center space-y-2 py-4">
            <XCircle className="w-14 h-14 mx-auto text-urgente" />
            <div className="text-xl font-bold text-urgente">CÓDIGO NO VÁLIDO</div>
            <p className="text-sm text-muted-foreground">
              El código <span className="font-mono">{codigo}</span> no corresponde a ninguna receta emitida.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
